import { User } from "types/user";
import type { ExtratoResponse } from "types/extrato";
import { api } from "./api";
import { dedupedFetch } from "./cache";
import { clearRegistrationUser } from "utils/fns";
import { dedupParticipantes } from "utils/participantes-dedup";
import {
  markGenericNotificationRead,
  markReadNotification,
} from "./notification.service";
import queries from "./queries";

const getRealizations = async (player: User, filter?: any): Promise<any> => {
  try {
    if (!player?._id) {
      throw new Error("Player ID is required");
    }

    const clearRegistration = clearRegistrationUser(player._id);

    const body = {
      player: clearRegistration,
      ...filter,
    };

    const { data } = await api.post("/find/realizations", body);
    if (Array.isArray(data)) {
      data.forEach((item) => {
        item.team_data = dedupParticipantes(item.team_data);
      });
      return data;
    }
  } catch (error: any) {
    throw error;
  }
};

const getSubscribedRealizations = async (player: User): Promise<any> => {
  try {
    const clearRegistration = clearRegistrationUser(player._id);

    const { data } = await api.post("/find/listSubscribedRealizations", {
      player: clearRegistration,
    });
    if (!Array.isArray(data)) {
      return null;
    } else {
      return data;
    }
  } catch (error: any) {
    throw error;
  }
};

const newRealization = async (
  body: any,
  realization: any,
  concorrentes: any,
  notificationId?: any
): Promise<any> => {
  try {
    body["id_realizacao"] = realization._id;
    const { data } = await api.post("/database/inscricao__c", body);

    if (!data) {
      throw new Error("Falha ao criar inscrição");
    }

    await newIndication({
      player: body.player,
      id_inscricao: data._id,
      id_realizacao: realization._id,
      dt_aceite: new Date(),
      rawPlayer: body.rawPlayer,
      concorrentes_info: concorrentes,
    });

    if (!!notificationId && notificationId !== "") {
      await markReadNotification(notificationId);
    }

    api.post(
      "/database/insc_partic_premio__c/bulk",
      realization.team_data
        .filter((item: any) => item._id !== body.player)
        .map((item: any) => {
          return {
            player: item._id,
            rawPlayer: item.chave,
            id_inscricao: data._id,
            id_realizacao: realization._id,
            concorrentes_info: [],
          };
        })
    );
  } catch (error: any) {
    console.error("Erro em newRealization:", error.message);
    throw error;
  }
};
// editRealization
const editRealization = async (body: any, concorrentes: any): Promise<any> => {
  try {
    await api.put("/database/inscricao__c", body);

    // await editIndication({
    //     _id:concorrentes._id,
    //     player:body.player,
    //     id_inscricao:body._id,
    //     id_realizacao:body.id_realizacao,
    //     concorrentes_info:concorrentes.itens
    // })
  } catch (error: any) {
    throw error;
  }
};

const newIndication = async (data: any): Promise<any> => {
  try {
    return await api.post("/database/insc_partic_premio__c", data);
  } catch (error: any) {
    throw error;
  }
};

const editIndication = async (
  body: any,
  notificationId?: any
): Promise<any> => {
  try {
    if (!!notificationId && notificationId !== "") {
      await markReadNotification(notificationId);
    } else {
      await markGenericNotificationRead(body);
    }
    return await api.put("/database/insc_partic_premio__c", body);
  } catch (error: any) {
    throw error;
  }
};

const isIndicated = async (itemId: any): Promise<any> => {
  try {
    const query = [
      {
        $match: {
          _id: itemId,
        },
      },
    ];
    const { data } = await api.post(
      "/database/insc_partic_premio__c/aggregate",
      query
    );

    if (!Array.isArray(data)) {
      return null;
    } else {
      return data[0];
    }
  } catch (error: any) {
    throw error;
  }
};

const isSubscribedRealization = async (
  realizationId: any,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userId: string
): Promise<any> => {
  try {
    // Leitura via prepared aggregate: o campo `player` é derivado do player
    // logado (context.player) no servidor; id_realizacao continua param.
    const { data } = await api.post("/find/inscricao_by_player_realizacao", {
      idRealizacao: realizationId,
    });

    if (Array.isArray(data)) {
      return data[0].player ? data[0] : null;
    } else {
      return null;
    }
  } catch (error: any) {
    throw error;
  }
};

const isSubscribed = async (
  realizationId: string,
  player: string
): Promise<any> => {
  try {
    const query = [
      {
        $match: {
          $and: [{ id_realizacao: realizationId }, { player: player }],
        },
      },
    ];

    const { data } = await api.post(
      "database/insc_partic_premio__c/aggregate",
      query
    );

    if (Array.isArray(data)) {
      return data[0].player ? data[0] : null;
    } else {
      return null;
    }
  } catch (error: any) {
    throw error;
  }
};

const countRealizations = async () => {};

const getCategories = async () => {
  try {
    const query = [
      { $match: {} },
      { $group: { _id: "$categoria" } },
      { $sort: { _id: 1 } },
    ];
    const { data } = await api.post(
      "database/task_category__c/aggregate",
      query
    );
    return data;
  } catch (error: any) {
    throw error;
  }
};

const getConfirmedPlayers = async (realizationId: string): Promise<any> => {
  try {
    const query = [
      {
        $match: {
          id_realizacao: realizationId,
          dt_aceite: { $exists: true, $ne: null },
        },
      },
      {
        $group: {
          _id: null,
          confirmados: { $addToSet: "$rawPlayer" },
        },
      },
    ];
    const { data } = await api.post(
      "/database/insc_partic_premio__c/aggregate",
      query
    );

    if (!Array.isArray(data)) {
      return null;
    } else {
      return data[0];
    }
  } catch (error: any) {
    throw error;
  }
};

const getPlayerslist = async (id_realization: string): Promise<any> => {
  try {
    const { data } = await api.post("/find/playersByRealization", {
      id_realizacao: id_realization,
    });
    if (!Array.isArray(data)) {
      return null;
    } else {
      return data[0];
    }
  } catch (error: any) {
    throw error;
  }
};

const onlyWinnersIds = async (): Promise<string[]> => {
  try {
    const { data } = await api.post(
      "/find/listOnlyWinnersRealization",
      {},
      {
        headers: { Range: `items=0-500` },
      }
    );

    if (!Array.isArray(data)) {
      return [];
    }

    return data.flatMap((item) =>
      item.resultado.map((res: any) => res.id_realizacao)
    );
  } catch (error: any) {
    throw error;
  }
};

const getWinners = async () => {
  try {
    const { data } = await api.post(
      "/find/realizationforstep2",
      {},
      {
        headers: {
          Range: `items=0-500`,
          Authorization: `Basic Njc4MDBjODJmYjZjZDc3MjIyNDY5NTg1OjY4OGMzYjVkMjllMzk3NTM1MGE4NWFjMQ==`,
        },
      }
    );

    if (!Array.isArray(data)) {
      return null;
    }

    const categories = Array.from(new Set(data.map((item) => item.categoria)));

    const grouped = categories.map((category) => {
      const realizations = data.filter((item) => item.categoria === category);

      return {
        categoryName: category,
        realizations,
      };
    });

    return grouped;
  } catch (error: any) {
    throw error;
  }
};

const getWinnersPublic = async () => {
  try {
    const { data } = await api.post(
      "/find/realizationforstep2",
      {},
      {
        headers: {
          Range: `items=0-500`,
          //dev
          //Authorization: `Basic Njc4MDBjODJmYjZjZDc3MjIyNDY5NTg1OjY4OGMzYjVkMjllMzk3NTM1MGE4NWFjMQ==`,

          //prod
          Authorization: `Basic Njc5ODU5ODJlNTEwODM1Yzc5NDlmMmE1OjY4OGNlMTgzMjllMzk3NTM1MGE4NjkwMw==`,
        },
      }
    );

    if (!Array.isArray(data)) {
      return null;
    }

    const categories = Array.from(new Set(data.map((item) => item.categoria)));

    const grouped = categories.map((category) => {
      const realizations = data.filter((item) => item.categoria === category);

      return {
        categoryName: category,
        realizations,
      };
    });

    return grouped;
  } catch (error: any) {
    throw error;
  }
};

const getExtrato = async (rawPlayer: string): Promise<ExtratoResponse> => {
  // matrícula numérica limpa = chave de junção das coleções do extrato
  const chave = clearRegistrationUser(rawPlayer);
  return dedupedFetch(
    `extrato-${chave}`,
    async () => {
      const { data } = await api.post("find/extrato", { rawPlayer: chave });
      const doc = Array.isArray(data) ? data[0] : null;
      return doc ?? { iniciativas: [], totais: { bruto: 0, ir: 0, liquido: 0 } };
    },
    60_000
  );
};

const getFinalResult = async (user: User) => {
  try {
    const { data } = await api.post("find/resultado_final", {});

    if (!Array.isArray(data) || data.length === 0) return { mine: [], others: [] };

    const normalized = data.map((item: any) => ({
      ...item,
      categoria: item.categoryPremio,
      id_realizacao: item._id,
      dt_aceite: item.dt_inscricao ?? item.updated ?? null,
      isMy:
        item.owner === user._id ||
        item.team?.some((t: any) => t._id === user._id) ||
        false,
    }));

    const mine = normalized.filter((item) => item.isMy);

    const categories = Array.from(
      new Set(normalized.map((item) => item.categoria as string))
    );

    const others = categories.map((category) => ({
      categoryName: category,
      realizations: normalized
        .filter((item) => item.categoria === category)
        .slice(0, 3),
    }));

    return { mine, others };
  } catch (error) {
    throw error;
  }
};

const indicatedRanking = async (realizationIds: Array<string>) => {
  try {
    const query = queries.ranking_indicate;

    query.unshift({
      $match: {
        id_realizacao: {
          $in: realizationIds,
        },
      },
    });
    const { data } = await api.post(
      "database/insc_partic_premio__c/aggregate",
      query
    );

    if (!Array.isArray(data)) {
      return [];
    } else {
      return data.map((item) => {
        return {
          concorrentes_info: item.concorrentes_info,
          id_realizacao: item.id_realizacao,
        };
      });
    }
  } catch (error) {
    throw error;
  }
};

export {
  getRealizations,
  getSubscribedRealizations,
  newRealization,
  editRealization,
  newIndication,
  editIndication,
  isSubscribed,
  isIndicated,
  isSubscribedRealization,
  getCategories,
  getConfirmedPlayers,
  getPlayerslist,
  getWinners,
  getExtrato,
  getFinalResult,
  getWinnersPublic,
};
